/**
 * The attendance board's class picker is the term that contains the day on
 * screen. Other quarters stay out of the list, and saving only changes the
 * classes from that term.
 */

export interface BoardBlock {
  id: string;
  attendanceEnabled: boolean;
  term?: {
    id: string;
    name?: string | null;
    startsOn?: unknown;
    endsOn?: unknown;
  } | null;
}

function isoDate(value: unknown): string | null {
  if (value == null || value === '') return null;
  return String(value).slice(0, 10);
}

export function termCovers(term: BoardBlock['term'], date: string): boolean {
  const startsOn = isoDate(term?.startsOn);
  const endsOn = isoDate(term?.endsOn);
  if (!startsOn || !endsOn) return false;
  return startsOn <= date && date <= endsOn;
}

/** Attendance blocks in the term that contains this date, plus blocks with no term. */
export function blocksOnBoard<T extends BoardBlock>(blocks: readonly T[], date: string): T[] {
  return blocks.filter((block) => (
    block.attendanceEnabled && (block.term == null || termCovers(block.term, date))
  ));
}

export function currentTermName(blocks: readonly BoardBlock[], date: string): string | null {
  const term = blocks.find((block) => block.attendanceEnabled && termCovers(block.term, date))?.term;
  return term?.name?.trim() || null;
}

/** Checks to show when the picker opens. A missing saved list means every visible class. */
export function draftBoardIds(visibleIds: readonly string[], savedIds: readonly string[] | null): Set<string> {
  if (savedIds == null) return new Set(visibleIds);
  const visible = new Set(visibleIds);
  return new Set(savedIds.filter((id) => visible.has(id)));
}

/**
 * Replace the visible term's choices and leave every other block as it was.
 * Null means no restriction: every attendance-enabled block.
 */
export function nextBoardIds({
  allIds,
  visibleIds,
  draftIds,
  savedIds,
}: {
  allIds: readonly string[];
  visibleIds: readonly string[];
  draftIds: ReadonlySet<string>;
  savedIds: readonly string[] | null;
}): string[] | null {
  const visible = new Set(visibleIds);
  const base = savedIds == null ? allIds : savedIds;
  const next = new Set(base.filter((id) => !visible.has(id)));
  for (const id of draftIds) {
    if (visible.has(id)) next.add(id);
  }
  const everyClass = allIds.length > 0 && allIds.every((id) => next.has(id));
  return everyClass ? null : [...next];
}
